<script setup lang="ts" generic="T extends Record<string, any>">
import { onKeyStroke, ref } from '#imports'

interface Props {
  list?: T[]
  listKey?: keyof T
}

const props = withDefaults(defineProps<Props>(), {
  list: () => [],
  listKey: 'key',
})

const emits = defineEmits<{
  selected: [T]
}>()

const selectedIndex = ref<number | null>(null)
const listContainerRef = ref<HTMLElement | null>(null)

const handleKeyDown = (event: KeyboardEvent) => {
  const keyToListen = ['ArrowDown', 'ArrowUp', 'Enter']
  if (!props.list.length || !keyToListen.includes(event.key)) {
    return
  }

  if (selectedIndex.value === null) {
    selectedIndex.value = 0
    return
  }

  switch (event.key) {
    case 'ArrowDown':
      selectedIndex.value = (selectedIndex.value + 1) % props.list.length
      break
    case 'ArrowUp':
      selectedIndex.value = (selectedIndex.value - 1 + props.list.length) % props.list.length
      break
    case 'Enter':
      if (listContainerRef.value) {
        listContainerRef.value.focus()
      }
      emits('selected', props.list[selectedIndex.value])
      break
  }
}

onKeyStroke(['ArrowDown', 'ArrowUp', 'Enter'], (e) => {
  handleKeyDown(e)
})
</script>

<template>
  <div
    ref="listContainerRef"
    tabindex="-1"
    role="listbox"
    @keydown.up.down.enter="handleKeyDown"
  >
    <button
      v-for="(item, index) in list"
      :key="item[listKey as keyof T]"
      class="flex flex-col md:p-2 w-full hover:bg-gray-50 rounded focus:border focus:shadow py-1 items-start"
      :class="{ 'shadow-[0_0_0_1px_rgb(0,0,0)]': index === selectedIndex }"
      :aria-selected="index === selectedIndex"
      role="option"
      @click.prevent.stop="emits('selected', item)"
      @focus="selectedIndex = index"
    >
      <slot :item="item" />
    </button>
  </div>
</template>
